import { styled } from 'styled-components'
import { onMediumScreen, onSmallScreen } from 'mixtiles-web-common'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayMedium};
`

export const ContentContainer = styled.div`
  padding: 44px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${onMediumScreen()} {
    padding: 32px 0px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
  }
`

export const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  font-style: normal;
  line-height: 28px;
  letter-spacing: 0;
  color: ${({ theme }) => theme.colors.black};
  margin: 0 0 24px 0;
  text-align: center;
  vertical-align: middle;
  max-width: 310px;

  ${onSmallScreen()} {
    max-width: 324px;
  }

  ${onMediumScreen()} {
    flex-basis: 248px;
    margin: 0;
    text-align: left;
    flex-shrink: 0;
    max-width: none;
    flex-basis: unset;

    font-weight: 400;
    font-style: 400 Regular;
    font-size: 24px;
    leading-trim: none;
    line-height: 28px;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
  }
`

export const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 324px;

  ${onMediumScreen()} {
    flex-shrink: 0;
    width: 400px;
  }
`

export const DynamicContentWrapper = styled.div<{ $isVisible: boolean }>`
  max-height: ${({ $isVisible }) => ($isVisible ? '120px' : '0px')};
  overflow: hidden;
  transition: max-height 0.4s ease;

  ${onMediumScreen()} {
    max-height: ${({ $isVisible }) => ($isVisible ? '100px' : '0px')};
  }
`

export const InputContainer = styled.div<{ $hasError?: boolean }>`
  display: flex;
  width: 100%;
  max-width: 500px;
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.errorRed : theme.colors.grayMedium};
  border-radius: 12px;
  overflow: hidden;
  transition: border-color 0.2s ease;

  ${onMediumScreen()} {
    max-width: 500px;
    margin-bottom: 0;
    flex-shrink: 0;
  }
`

export const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  height: 56px;
`

export const InputLabel = styled.label<{ $hasValue: boolean }>`
  position: absolute;
  left: 20px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grayDark};
  pointer-events: none;
  transition: all 0.2s ease;
  z-index: 1;

  ${({ $hasValue }) =>
    $hasValue
      ? `
        font-size: 12px;
        top: 12px;
        transform: translateY(0px);
      `
      : `
        font-size: 16px;
        top: 50%;
        transform: translateY(-50%);
      `}
`

export const EmailInput = styled.input<{ $hasError?: boolean }>`
  flex: 1;
  padding: 30px 20px 8px 20px;
  border: none;
  outline: none;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.black};
  border-radius: 12px;
  background-color: transparent;
  position: relative;
  z-index: 2;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grayDark};
  }

  &:focus {
    outline: none;
  }

  /* Chrome autofill */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.black} !important;
    background-color: transparent !important;
    background-image: none !important;
    background-clip: content-box !important;
    color: ${({ theme }) => theme.colors.black} !important;
    transition: background-color 5000s ease-in-out 0s !important;
    -webkit-transition: background-color 5000s ease-in-out 0s !important;
    font-family: inherit !important;
    font-size: 16px !important;
    caret-color: ${({ theme }) => theme.colors.black} !important;
  }

  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
  }

  &:focus:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.black} !important;
  }

  ${onMediumScreen()} {
    cursor: pointer;
  }
`

export const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  width: 46px;
  border-radius: 10px;
  margin: 5px;
  background-color: ${({ theme }) => theme.colors.primary};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.primary};
    cursor: not-allowed;
    opacity: 0.8;
  }

  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1); /* Makes the image white */
  }

  ${onMediumScreen()} {
    img {
      width: 24px;
      height: 24px;
    }
  }
`

export const TermsText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grayDark};
  margin: 16px 0 0 0;
  text-align: center;
  line-height: 1.4;
  max-width: 500px;
  opacity: 0;
  transform: translateY(10px);
  animation: slideUpFadeTerms 0.4s ease-out 0.2s forwards;

  @keyframes slideUpFadeTerms {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${onMediumScreen()} {
    font-size: 14px;
    max-width: 500px;
    text-align: left;
    margin: 12px 0 0 0;
  }
`

export const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 310px;
  padding: 44px 0px;

  ${onMediumScreen()} {
    padding: 32px 40px;
    flex-direction: row;
    width: unset;
  }
`

export const SuccessIconContainer = styled.div`
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

export const SuccessMessage = styled.p`
  font-weight: 600;
  font-style: normal;
  font-size: 24px;
  line-height: 28px;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;

  ${onMediumScreen()} {
    font-weight: 400;
    font-style: 600 SemiBold;
    font-size: 22px;
    leading-trim: none;
    line-height: 28px;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
  }
`
